import React from 'react'

import GlyphIcon, { type GlyphIconProps } from 'components/common/GlyphIcon'
import Icon, { type IconName, type IconProps } from 'components/Icon'
import { muiTheme } from 'mui-theme'
import theme from 'theme'
import {
  type AttackCommand,
  type AttackName
} from 'types/api/attacks/details/targeting'

const ATTACK_COMMAND_ICON_NAMES: Record<AttackCommand, IconName> = {
  /**
   * AttackCommand keys
   */
  // "Resource"
  cpu: 'solidMicrochip',
  gpu: 'solidMicrochipAi',
  disk: 'regularCompactDisc',
  io: 'regularArrowUpArrowDown',
  memory: 'solidMemory',
  process_exhaustion: 'regularFaceExhaling',
  // "State"
  process_killer: 'regularSkull',
  shutdown: 'solidPowerOff',
  time_travel: 'solidClockRotateLeft',
  // "Network"
  blackhole: 'solidGalaxy',
  collect_certs: 'regularFileCertificate',
  dns: 'regularGlobe',
  latency: 'solidTurtle',
  packet_loss: 'regularFillDrip'
} as const

const ATTACK_NAME_ICON_NAMES: Record<AttackName, IconName> = {
  // "Resource"
  CPU: 'solidMicrochip',
  GPU: 'solidMicrochipAi',
  Disk: 'regularCompactDisc',
  IO: 'regularArrowUpArrowDown',
  Memory: 'solidMemory',
  'Process Exhaustion': 'regularFaceExhaling',
  // "State"
  'Process Killer': 'regularSkull',
  Shutdown: 'solidPowerOff',
  'Time Travel': 'solidClockRotateLeft',
  // "Network"
  Blackhole: 'solidGalaxy',
  'Certificate Expiry': 'regularFileCertificate',
  DNS: 'regularGlobe',
  Latency: 'solidTurtle',
  'Packet Loss': 'regularFillDrip'
} as const

const OTHER_FAULT_ICON_NAMES = {
  awsExternalAction: 'awsIcon',
  failureFlag: 'solidFlag',
  host: 'regularServer',
  scenario: 'solidListTimeline',
  zone: 'regularEarthAmericas'
} as const

/**
 * Map ids returned from API to above keys
 *
 * Service and Service Dependency Tests
 */
const FAULT_ICON_NAME_FROM_KEYS = {
  'blackhole-test': ATTACK_COMMAND_ICON_NAMES.blackhole,
  'certificate-expiry': ATTACK_COMMAND_ICON_NAMES.collect_certs,
  'latency-test': ATTACK_COMMAND_ICON_NAMES.latency,
  'scaling-cpu': ATTACK_COMMAND_ICON_NAMES.cpu,
  'scaling-memory': ATTACK_COMMAND_ICON_NAMES.memory,
  'single-host-failure': OTHER_FAULT_ICON_NAMES.host,
  'zone-failure': OTHER_FAULT_ICON_NAMES.zone
} as const

/**
 * Combine into a full map of keys to icon names
 */
export type FaultName =
  | AttackCommand
  | AttackName
  | keyof typeof OTHER_FAULT_ICON_NAMES
  | keyof typeof FAULT_ICON_NAME_FROM_KEYS

const FAULT_NAME_ICON_MAP: Record<FaultName, IconName> = {
  ...ATTACK_COMMAND_ICON_NAMES,
  ...ATTACK_NAME_ICON_NAMES,
  ...OTHER_FAULT_ICON_NAMES,
  ...FAULT_ICON_NAME_FROM_KEYS
} as const

const FALLBACK_FAULT_ICON_NAME: IconName = 'solidFlask'

function getIconName(
  fault: string | undefined,
  fallback?: FaultName
): IconName {
  const key = fault ?? fallback
  const iconName = key && FAULT_NAME_ICON_MAP[key]

  return iconName ?? FALLBACK_FAULT_ICON_NAME
}

export const FaultIcon: React.FC<{
  color?: string
  faultName: string | undefined
}> = ({ color, faultName, ...rest }) => {
  const iconName = getIconName(faultName)

  return (
    <Icon
      aria-label={`icon-${iconName}`}
      css={{
        width: theme.space[6],
        fontSize: theme.fontSizes[3],
        color: color ?? theme.colors.purple[0]
      }}
      icon={iconName}
      {...rest}
    />
  )
}

export const FaultIconWithBorder: React.FC<
  GlyphIconProps & {
    borderColor?: string
    faultName: string | undefined
    iconColor?: string
    iconProps?: IconProps
  }
> = ({
  backgroundColor,
  borderColor,
  children,
  iconColor,
  iconProps,
  faultName,
  ...rest
}) => {
  return (
    <GlyphIcon
      backgroundColor={backgroundColor ?? muiTheme.palette.common.white}
      css={{
        '& .glyph-circle': {
          border: theme.border.base,
          borderColor: borderColor ?? muiTheme.palette.grey['900']
        }
      }}
      {...rest}
    >
      <FaultIcon color={iconColor} faultName={faultName} {...iconProps} />
      {children}
    </GlyphIcon>
  )
}
